import { useScienceTutorSection } from './scienceTutorContext';
import { useState } from 'react';

import {
  GOLD_FOIL_OBSERVATION,
  PLUM_PUDDING_PREDICTION,
  REAL_BOUNCE_BACK_RATIO,
  countOutcomes,
  scatterTracks,
  type ScatterModel,
  type ScatterOutcome,
} from './atomicModels';
import { PredictionQuestion } from './AtomicModelsQuestion';

const VIEW_WIDTH = 360;
const VIEW_HEIGHT = 220;
const FOIL_X = 200;
const CENTRE_Y = VIEW_HEIGHT / 2;
const ATOM_RADIUS = 90;
const EXIT_LENGTH = 150;
const ELECTRON_ANGLES = [20, 75, 130, 190, 245, 300];

const OUTCOME_LABELS: Record<ScatterOutcome, string> = {
  straight: 'Straight through',
  deflected: 'Deflected',
  bounced: 'Bounced back',
};

const OUTCOME_STROKES: Record<ScatterOutcome, { className: string; dash?: string }> = {
  straight: { className: 'text-brand-sky' },
  deflected: { className: 'text-brand-sunshine', dash: '6 4' },
  bounced: { className: 'text-brand-coral', dash: '2 3' },
};

function ScatterView({ model }: { model: ScatterModel }) {
  const tracks = scatterTracks(model);
  const counts = countOutcomes(tracks);
  const label =
    model === 'nuclear'
      ? `Nuclear model: ${counts.straight} tracks straight, ${counts.deflected} deflected, ${counts.bounced} bounced back`
      : `Plum pudding model: all ${counts.straight} tracks pass almost straight through`;

  return (
    <figure className="min-w-0">
      <svg viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`} className="w-full" role="img" aria-label={label}>
        {model === 'plum-pudding' ? (
          <g>
            <circle cx={FOIL_X} cy={CENTRE_Y} r={ATOM_RADIUS} className="fill-wash-coral" />
            {ELECTRON_ANGLES.map((angle) => {
              const radians = (angle * Math.PI) / 180;
              return (
                <text
                  key={angle}
                  x={FOIL_X + 0.55 * ATOM_RADIUS * Math.cos(radians)}
                  y={CENTRE_Y + 0.55 * ATOM_RADIUS * Math.sin(radians) + 4}
                  textAnchor="middle"
                  className="fill-ink text-[12px] font-extrabold"
                >
                  −
                </text>
              );
            })}
          </g>
        ) : (
          <g>
            <circle cx={FOIL_X} cy={CENTRE_Y} r={ATOM_RADIUS} fill="none" stroke="currentColor" strokeDasharray="3 5" className="text-hairline" />
            <circle cx={FOIL_X} cy={CENTRE_Y} r="4" className="fill-brand-coral" />
          </g>
        )}
        {tracks.map((track) => {
          const y = CENTRE_Y + track.impactParameter * ATOM_RADIUS;
          const radians = (track.deflectionDeg * Math.PI) / 180;
          const endX = FOIL_X + EXIT_LENGTH * Math.cos(radians);
          const endY = y + EXIT_LENGTH * Math.sin(radians);
          const stroke = OUTCOME_STROKES[track.outcome];
          return (
            <polyline
              key={track.impactParameter}
              points={`8,${y} ${FOIL_X},${y} ${endX},${endY}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeDasharray={stroke.dash}
              className={stroke.className}
            />
          );
        })}
        <text x="8" y="14" className="fill-slate2 text-[10px] font-bold">
          Alpha particles →
        </text>
        <text x={FOIL_X + 6} y={VIEW_HEIGHT - 6} className="fill-slate2 text-[10px] font-bold">
          Gold atom (not to scale)
        </text>
      </svg>
      <figcaption className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-ink-soft">
        {(Object.keys(OUTCOME_LABELS) as ScatterOutcome[]).map((outcome) => (
          <span key={outcome} className="rounded-full border border-hairline bg-white px-3 py-1">
            {OUTCOME_LABELS[outcome]}: {counts[outcome]}
            {outcome === 'straight' ? ' (solid line)' : outcome === 'deflected' ? ' (dashed)' : ' (dotted)'}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

export function GoldFoilExperiment() {
  const [plumChecked, setPlumChecked] = useState(false);
  const [observedChecked, setObservedChecked] = useState(false);
  useScienceTutorSection('GoldFoilExperiment', { plumChecked, observedChecked });

  return (
    <section
      aria-labelledby="gold-foil-heading"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
        Gold foil experiment · 1909
      </p>
      <h2 id="gold-foil-heading" className="mt-2 text-2xl font-extrabold text-ink">
        Predict, then fire the alpha particles
      </h2>
      <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate2">
        Fast, positive alpha particles were fired at gold foil about 1000 atoms thick. A
        detector screen around the foil recorded where they went.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <p className="text-sm font-extrabold text-ink">Step 1 · Use the plum pudding model</p>
          <PredictionQuestion
            question={PLUM_PUDDING_PREDICTION}
            checkLabel="Show the plum pudding prediction"
            onChecked={() => setPlumChecked(true)}
          />
          {plumChecked ? (
            <div className="rounded-[1.5rem] border border-brand-sky/25 bg-wash-sky p-4">
              <p className="text-xs font-extrabold uppercase tracking-[0.13em] text-brand-sky">
                Plum pudding model result
              </p>
              <ScatterView model="plum-pudding" />
            </div>
          ) : null}
        </div>

        <div className="min-w-0 space-y-4">
          <p className="text-sm font-extrabold text-ink">Step 2 · Predict the real result</p>
          {plumChecked ? (
            <PredictionQuestion
              question={GOLD_FOIL_OBSERVATION}
              checkLabel="Show the observed result"
              onChecked={() => setObservedChecked(true)}
            />
          ) : (
            <p className="rounded-2xl bg-surface px-4 py-3 text-sm font-medium text-slate2">
              Check the plum pudding prediction first.
            </p>
          )}
          {observedChecked ? (
            <div className="rounded-[1.5rem] border border-brand-mint/35 bg-wash-mint p-4">
              <p className="text-xs font-extrabold uppercase tracking-[0.13em] text-brand-mint">
                Observed · explained by the nuclear model
              </p>
              <ScatterView model="nuclear" />
              <p className="mt-3 text-xs font-medium leading-5 text-slate2">
                Close passes are over-represented so rare events are visible. In the real experiment
                about {REAL_BOUNCE_BACK_RATIO} alpha particles bounced back.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
